import TableRowsIcon from "@mui/icons-material/TableRows";
import { useQueries } from "@tanstack/react-query";
import React from "react";
import { Link } from "react-router-dom";

import {
  MIN_SPARKLINE_POINTS,
  pickFeaturedTiles,
  sparklineRequestFromSchema,
  sparklineValues,
  tileCandidates,
  type TileCollection,
} from "../lib/demo-table-tiles";
import { getApi } from "../lib/get-api";
import { humanize, type Field } from "../lib/schema-field";
import { useAuth } from "../lib/useAuth";

export type { TileCollection };

const compact = new Intl.NumberFormat("en", { notation: "compact" });
const exact = new Intl.NumberFormat("en");

const collectionPath = (datasourceId: string, name: string) =>
  `/datasources/${datasourceId}/collections/${encodeURIComponent(name)}`;

/**
 * A count and a shape, not a chart.
 *
 * Values are a month-bucketed COUNT(*) in calendar order, normalised per tile
 * so neighbouring tables at different magnitudes still read as a shape. The
 * number lives in the caption; the line only carries direction over time.
 */
const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
  const width = 120;
  const height = 28;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : width;

  const points = values
    .map((value, index) => {
      const x = index * step;
      const y = height - 1 - ((value - min) / span) * (height - 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="mt-2 h-7 w-full"
      aria-hidden="true"
      focusable="false"
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--od-primary)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        opacity="0.85"
      />
    </svg>
  );
};

const TableTile: React.FC<{
  datasourceId: string;
  collection: TileCollection;
  rows?: number;
  values: number[];
  loading?: boolean;
}> = ({ datasourceId, collection, rows, values, loading }) => (
  <Link
    to={collectionPath(datasourceId, collection.name)}
    title={collection.name}
    className="flex flex-col rounded-md border border-od-border bg-od-surface p-3 transition-colors hover:border-od-primary"
  >
    <span className="truncate text-sm font-medium text-od-text">
      {humanize(collection.name)}
    </span>

    <span className="mt-0.5 flex h-4 items-center text-xs text-od-muted">
      {loading ? (
        <span className="inline-block h-2 w-16 animate-pulse rounded bg-od-surface-2" />
      ) : rows === undefined ? (
        <span>Open table</span>
      ) : (
        <span title={`${exact.format(rows)} records`}>
          {compact.format(rows)} {rows === 1 ? "record" : "records"}
        </span>
      )}
    </span>

    {values.length >= MIN_SPARKLINE_POINTS ? (
      <Sparkline values={values} />
    ) : (
      <span className="mt-2 block h-7" aria-hidden="true" />
    )}
  </Link>
);

/**
 * The demo's tables, as a short row of real tiles plus a disclosure holding the
 * rest.
 *
 * Featured tiles are picked after the aggregates resolve: empty tables drop
 * out, and a sparkline is only drawn for a month-bucketed COUNT(*) with at
 * least three points. The full list stays one click away.
 */
export const DemoTableTiles: React.FC<{
  datasourceId: string;
  /** Already ranked: the caller owns the ordering. */
  collections: TileCollection[];
  /** How many get a tile. The rest go into the disclosure. */
  featured?: number;
}> = ({ datasourceId, collections, featured = 4 }) => {
  const token = useAuth((state) => state.token);
  const candidates = React.useMemo(
    () => tileCandidates(collections),
    [collections],
  );

  const queries = useQueries({
    queries: candidates.map((collection) => {
      const request = sparklineRequestFromSchema(
        (collection.fields ?? []) as unknown as Field[],
      );
      return {
        queryKey: ["demoTile", datasourceId, collection.name, request],
        queryFn: async () => {
          const api = getApi({ token });
          const response = await api.data.aggregateSeries(
            datasourceId,
            collection.name,
            {
              groupBy: request!.groupBy,
              bucket: request!.bucket,
            },
          );
          return response.data;
        },
        enabled: !!datasourceId && !!collection.name && !!request,
        staleTime: 300000,
        retry: false,
      };
    }),
  });

  if (collections.length === 0) return null;

  const loaded = candidates.map((collection, index) => {
    const data = queries[index]?.data;
    return {
      collection,
      rows: data?.rows,
      values: sparklineValues(data?.points),
    };
  });

  const pending = queries.some((query) => query.isLoading);
  const tiles = pending
    ? candidates.slice(0, featured).map((collection) => ({
        collection,
        rows: undefined as number | undefined,
        values: [] as number[],
      }))
    : pickFeaturedTiles(loaded, featured);

  return (
    <div className="mt-4 border-t border-od-border pt-4">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <TableTile
            key={tile.collection.id ?? tile.collection.name}
            datasourceId={datasourceId}
            collection={tile.collection}
            rows={tile.rows}
            values={tile.values}
            loading={pending}
          />
        ))}
      </div>

      {collections.length > tiles.length ? (
        <details className="group mt-3">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-md text-xs text-od-muted hover:text-od-primary-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-od-primary">
            <TableRowsIcon sx={{ fontSize: 12 }} />
            All {collections.length} tables
          </summary>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {collections.map((collection) => (
              <Link
                key={collection.id ?? collection.name}
                to={collectionPath(datasourceId, collection.name)}
                title={collection.name}
                className="rounded-md border border-od-border bg-od-surface px-2 py-0.5 text-xs text-od-muted transition-colors hover:border-od-primary hover:text-od-primary-text"
              >
                {humanize(collection.name)}
              </Link>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
};

export default DemoTableTiles;
